import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Site } from 'src/app/models/centrex/site';
import { SiteService } from 'src/app/services/centrex/site.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { MatButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { Instance } from 'src/app/models/centrex/instance';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-site-form',
    templateUrl: './site-form.component.html',
    styleUrls: ['./site-form.component.scss'],
    imports: [FaIconComponent, ReactiveFormsModule, MatCard, MatCardContent, MatFormField, MatLabel, MatInput, MatButton, RouterLink, TranslateModule]
})
export class SiteFormComponent implements OnInit {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);
    // Icône
    public faAngleRight = faAngleRight;

    siteInformations: Site;
    siteForm: UntypedFormGroup;
    isCreation: boolean;

    private previousInstance: Instance;

    constructor(
        private route: ActivatedRoute,
        private siteService: SiteService,
        private organizationsService: OrganizationsService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.createForm({} as Site);

        this.previousInstance = this.instanceService.instance();

        effect(() => {
            if (this.instanceService.instance() && this.previousInstance.id !== this.instanceService.instance().id) {
                this.router.navigate(['/sites']);
            }
        });
    }

    ngOnInit(): void {
        let siteId: string;

        // Récupère l'identifiant du site
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            siteId = params['id'];
        });

        this.loadDatas(siteId);
    }

    loadDatas(siteId: string) {
        this.spinner.show();

        if (siteId) {
            this.siteService
                .getSite(siteId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (site) => {
                        this.siteInformations = site;
                        this.isCreation = false;

                        // Créé le formulaire
                        this.createForm(this.siteInformations);
                    },
                    error: () => {
                        this.spinner.hide();
                    },
                });
        } else {
            this.isCreation = true;
            this.siteInformations = new Site();
            this.siteInformations.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';

            // Créé le formulaire
            this.createForm(this.siteInformations);
        }
    }

    createForm(site: Site) {
        this.siteForm = this.formbuilder.group({
            id: [site.id],
            organizationId: [site.organizationId],
            name: [site.name],
        });

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        if (this.isCreation) {
            // Appel de la méthode de création
            this.siteService
                .addSite(this.siteInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('Site.SuccessMessageCreateSite'));

                        // Redirige sur la page des sites
                        this.router.navigate(['/sites']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Site.ErrorMessageCreateSite'));

                        this.spinner.hide();
                    },
                });
        } else {
            this.siteService
                .updateSite(this.siteInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('Site.SuccessMessageUpdateSite'));

                        this.router.navigate(['/sites']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Site.ErrorMessageUpdateSite'));

                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.siteInformations.name = this.siteForm.value.name;
    }
}
